import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { 
  Gamepad2, 
  ArrowLeft, 
  Loader2, 
  Plus, 
  Trash2, 
  Play,
  ExternalLink,
  Gift,
  BarChart3,
  Percent,
  StopCircle,
  CircleDot,
  Ticket,
  Palette,
  Upload,
  Image,
  Video,
  X,
  Coins,
  CreditCard,
  Users,
  Euro
} from "lucide-react";
import { toast } from "sonner";
import { gamesAPI } from "../lib/api";
import api from "../lib/api";
import { getImageUrl } from "../lib/utils";

export default function EditGame() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [game, setGame] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [addingPrize, setAddingPrize] = useState(false);
  const [savingAppearance, setSavingAppearance] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingScratchLogo, setUploadingScratchLogo] = useState(false);
  const [uploadingScratchImage, setUploadingScratchImage] = useState(false);

  // Chance configuration state
  const [chanceConfig, setChanceConfig] = useState({
    price_per_chance: 2.0,
    min_purchase: 1,
    max_purchase: 10,
    allow_purchase: true
  });
  const [savingChanceConfig, setSavingChanceConfig] = useState(false);
  const [participantWallets, setParticipantWallets] = useState([]);
  const [addingChances, setAddingChances] = useState(false);
  const [newChance, setNewChance] = useState({ phone: "", amount: 1, reason: "Attribution manuelle" });

  const [appearance, setAppearance] = useState({
    background_type: "color",
    background_color: "#1E293B",
    background_image: "",
    background_video_url: "",
    primary_color: "#F59E0B",
    scratch_card_logo: "",
    scratch_card_image: "",
    // Result screen customization
    lose_image: "",
    lose_video_url: "",
    lose_messages: ["Pas de chance !", "Dommage.. c'était pas loin !", "Retentez votre chance !"]
  });

  const [newPrize, setNewPrize] = useState({ 
    name: "", 
    description: "", 
    code: "", 
    probability: 10,
    quantity: -1,
    image_url: "",
    result_image: "",
    result_video_url: ""
  });

  useEffect(() => {
    loadGame();
  }, [id]);

  const loadGame = async () => {
    try {
      const [gameRes, statsRes] = await Promise.all([
        gamesAPI.get(id),
        gamesAPI.getStats(id).catch(() => ({ data: null }))
      ]);
      setGame(gameRes.data);
      setStats(statsRes.data);
      
      // Load appearance settings
      setAppearance({
        background_type: gameRes.data.background_type || "color",
        background_color: gameRes.data.background_color || "#1E293B",
        background_image: gameRes.data.background_image || "",
        background_video_url: gameRes.data.background_video_url || "",
        primary_color: gameRes.data.primary_color || "#F59E0B",
        scratch_card_logo: gameRes.data.scratch_card_logo || "",
        scratch_card_image: gameRes.data.scratch_card_image || "",
        // Result screen customization
        lose_image: gameRes.data.lose_image || "",
        lose_video_url: gameRes.data.lose_video_url || "",
        lose_messages: gameRes.data.lose_messages || ["Pas de chance !", "Dommage.. c'était pas loin !", "Retentez votre chance !"]
      });

      // Load chance configuration for scratch games
      if (gameRes.data.game_type === "scratch") {
        try {
          const [configRes, walletsRes] = await Promise.all([
            gamesAPI.getChanceConfig(id),
            gamesAPI.getParticipantsWallets(id).catch(() => ({ data: [] }))
          ]);
          setChanceConfig(configRes.data);
          setParticipantWallets(walletsRes.data || []);
        } catch (e) {
          // Default config if not set
        }
      }
    } catch (error) {
      toast.error("Jeu non trouvé");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrize = async () => {
    if (!newPrize.name.trim()) {
      toast.error("Nom du lot requis");
      return;
    }
    setAddingPrize(true);
    try {
      await gamesAPI.addPrize(id, {
        ...newPrize,
        probability: newPrize.probability / 100 // Convert to decimal
      });
      toast.success("Lot ajouté");
      setNewPrize({ name: "", description: "", code: "", probability: 10, quantity: -1, image_url: "", result_image: "", result_video_url: "" });
      loadGame();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setAddingPrize(false);
    }
  };

  const handleRemovePrize = async (prizeId) => {
    try {
      await gamesAPI.removePrize(id, prizeId);
      toast.success("Lot supprimé");
      loadGame();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Handle background image upload
  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }
    
    setUploadingBg(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setAppearance(prev => ({ 
        ...prev, 
        background_image: response.data.url,
        background_type: "image"
      }));
      toast.success("Image téléchargée");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setUploadingBg(false);
    }
  };

  // Handle scratch card logo upload
  const handleScratchLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 2 Mo");
      return;
    }
    
    setUploadingScratchLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setAppearance(prev => ({ 
        ...prev, 
        scratch_card_logo: response.data.url
      }));
      toast.success("Logo de la carte téléchargé");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setUploadingScratchLogo(false);
    }
  };

  // Handle scratch card background image upload
  const handleScratchImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }
    
    setUploadingScratchImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setAppearance(prev => ({ 
        ...prev, 
        scratch_card_image: response.data.url
      }));
      toast.success("Image de la carte téléchargée");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setUploadingScratchImage(false);
    }
  };

  // Save chance configuration
  const handleSaveChanceConfig = async () => {
    setSavingChanceConfig(true);
    try {
      await gamesAPI.updateChanceConfig(id, chanceConfig);
      toast.success("Configuration des chances enregistrée");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingChanceConfig(false);
    }
  };

  // Add chances to participant
  const handleAddChances = async () => {
    if (!newChance.phone.trim()) {
      toast.error("Numéro de téléphone requis");
      return;
    }
    if (newChance.amount < 1 || newChance.amount > 100) {
      toast.error("Nombre de chances invalide (1-100)");
      return;
    }
    
    setAddingChances(true);
    try {
      const res = await gamesAPI.addChances(id, newChance);
      toast.success(res.data.message);
      setNewChance({ phone: "", amount: 1, reason: "Attribution manuelle" });
      // Reload wallets
      const walletsRes = await gamesAPI.getParticipantsWallets(id);
      setParticipantWallets(walletsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'attribution");
    } finally {
      setAddingChances(false);
    }
  };

  // Extract YouTube video ID
  const extractYoutubeId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url?.match(regex);
    return match ? match[1] : null;
  };

  // Save appearance settings
  const handleSaveAppearance = async () => {
    setSavingAppearance(true);
    try {
      await gamesAPI.update(id, appearance);
      toast.success("Apparence mise à jour");
      loadGame();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingAppearance(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      await gamesAPI.activate(id);
      toast.success("Jeu activé !");
      loadGame();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'activation");
    } finally {
      setActivating(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await gamesAPI.deactivate(id);
      toast.success("Jeu désactivé");
      loadGame();
    } catch (error) {
      toast.error("Erreur lors de la désactivation");
    }
  };

  const totalProbability = game?.prizes?.reduce((sum, p) => sum + (p.probability || 0) * 100, 0) || 0;
  const isActive = game?.status === "active";
  const isDraft = game?.status === "draft";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="outline" size="icon" className="rounded-full" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                game?.game_type === "wheel" 
                  ? "bg-gradient-to-br from-amber-400 to-orange-500"
                  : "bg-gradient-to-br from-emerald-400 to-teal-500"
              }`}>
                {game?.game_type === "wheel" ? (
                  <CircleDot className="w-5 h-5 text-white" />
                ) : (
                  <Ticket className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>{game?.title}</h1>
                <p className="text-sm text-muted-foreground">
                  {game?.game_type === "wheel" ? "Roue de la fortune" : "Carte à gratter"}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isActive ? (
              <Badge className="bg-green-100 text-green-700">Actif</Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-700">Brouillon</Badge>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-full"
              onClick={() => window.open(`/game/${id}`, '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Tester
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs defaultValue="prizes" className="space-y-6">
          <TabsList className="bg-card flex-wrap">
            <TabsTrigger value="prizes">
              <Gift className="w-4 h-4 mr-2" />
              Lots ({game?.prizes?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="appearance">
              <Palette className="w-4 h-4 mr-2" />
              Apparence
            </TabsTrigger>
            {game?.game_type === "scratch" && (
              <TabsTrigger value="chances">
                <Coins className="w-4 h-4 mr-2" />
                Chances
              </TabsTrigger>
            )}
            <TabsTrigger value="stats">
              <BarChart3 className="w-4 h-4 mr-2" />
              Statistiques
            </TabsTrigger>
          </TabsList>

          {/* Prizes Tab */}
          <TabsContent value="prizes">
            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Gift className="w-5 h-5" />
                  Configuration des lots
                </CardTitle>
                <CardDescription>
                  Définissez les lots et leurs probabilités de gain
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Probability indicator */}
                <div className="p-4 bg-muted/50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Probabilité totale de gain</span>
                    <span className={`text-sm font-bold ${totalProbability > 100 ? 'text-red-500' : 'text-green-600'}`}>
                      {totalProbability.toFixed(1)}%
                    </span>
                  </div>
                  <Progress 
                    value={Math.min(totalProbability, 100)} 
                    className={`h-2 ${totalProbability > 100 ? '[&>div]:bg-red-500' : '[&>div]:bg-green-500'}`}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    {100 - totalProbability > 0 
                      ? `${(100 - totalProbability).toFixed(1)}% de chance de ne rien gagner`
                      : totalProbability > 100 
                      ? "⚠️ La somme dépasse 100% - réduisez les probabilités"
                      : "100% de chance de gagner quelque chose"}
                  </p>
                </div>

                {/* Add prize form */}
                {(isDraft || isActive) && (
                  <div className="space-y-4 p-4 bg-muted/30 rounded-xl">
                    <h4 className="font-medium text-foreground">Ajouter un lot</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        placeholder="Nom du lot *"
                        value={newPrize.name}
                        onChange={(e) => setNewPrize({...newPrize, name: e.target.value})}
                        className="h-10 bg-white"
                        data-testid="prize-name-input"
                      />
                      <Input
                        placeholder="Code promo (optionnel)"
                        value={newPrize.code}
                        onChange={(e) => setNewPrize({...newPrize, code: e.target.value})}
                        className="h-10 bg-white"
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Probabilité (%)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={newPrize.probability}
                            onChange={(e) => setNewPrize({...newPrize, probability: parseFloat(e.target.value) || 0})}
                            className="h-10 bg-white"
                          />
                          <Percent className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Quantité (-1 = illimité)</Label>
                        <Input
                          type="number"
                          min="-1"
                          value={newPrize.quantity}
                          onChange={(e) => setNewPrize({...newPrize, quantity: parseInt(e.target.value) || -1})}
                          className="h-10 bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Image URL (optionnel)</Label>
                        <Input
                          placeholder="https://..."
                          value={newPrize.image_url}
                          onChange={(e) => setNewPrize({...newPrize, image_url: e.target.value})}
                          className="h-10 bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Image de résultat (fond quand gagné)</Label>
                        <Input
                          placeholder="https://... (image affichée après grattage)"
                          value={newPrize.result_image}
                          onChange={(e) => setNewPrize({...newPrize, result_image: e.target.value})}
                          className="h-10 bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Vidéo YouTube de résultat (optionnel)</Label>
                        <Input
                          placeholder="https://youtube.com/watch?v=..."
                          value={newPrize.result_video_url}
                          onChange={(e) => setNewPrize({...newPrize, result_video_url: e.target.value})}
                          className="h-10 bg-white"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleAddPrize}
                      disabled={addingPrize}
                      className="rounded-full bg-primary hover:bg-primary/90"
                      data-testid="add-prize-btn"
                    >
                      {addingPrize ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Ajouter le lot
                    </Button>
                  </div>
                )}

                {/* Prizes List */}
                <div className="space-y-3">
                  {game?.prizes?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      Aucun lot configuré. Ajoutez au moins un lot.
                    </p>
                  ) : (
                    game?.prizes?.map((prize) => (
                      <div 
                        key={prize.id} 
                        className="flex items-center justify-between p-4 bg-muted/30 rounded-xl group"
                      >
                        <div className="flex items-center gap-4">
                          {prize.image_url ? (
                            <img src={getImageUrl(prize.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Gift className="w-6 h-6 text-primary" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-foreground">{prize.name}</p>
                            {prize.code && (
                              <p className="text-sm text-muted-foreground">Code: {prize.code}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <Badge variant="secondary" className="mb-1">
                              {(prize.probability * 100).toFixed(1)}%
                            </Badge>
                            <p className="text-xs text-muted-foreground">
                              {prize.quantity === -1 ? "Illimité" : `${prize.remaining}/${prize.quantity}`}
                              {prize.won_count > 0 && ` • ${prize.won_count} gagné(s)`}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                            onClick={() => handleRemovePrize(prize.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Activate Card */}
            <Card className="bg-card mt-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1" style={{ fontFamily: 'Outfit' }}>
                      {isActive ? "Désactiver le jeu" : "Activer le jeu"}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {isActive 
                        ? "Le jeu ne sera plus accessible aux joueurs"
                        : game?.prizes?.length === 0 
                        ? "Ajoutez au moins un lot pour activer"
                        : totalProbability > 100 
                        ? "Réduisez les probabilités (max 100%)"
                        : "Rendre le jeu accessible aux joueurs"}
                    </p>
                  </div>
                  {isActive ? (
                    <Button
                      onClick={handleDeactivate}
                      variant="outline"
                      className="rounded-full"
                    >
                      <StopCircle className="w-4 h-4 mr-2" />
                      Désactiver
                    </Button>
                  ) : (
                    <Button
                      onClick={handleActivate}
                      disabled={!game?.prizes?.length || activating || totalProbability > 100}
                      className="rounded-full bg-green-600 hover:bg-green-700"
                      data-testid="activate-game-btn"
                    >
                      {activating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Activer
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance">
            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Palette className="w-5 h-5" />
                  Personnalisation de l'arrière-plan
                </CardTitle>
                <CardDescription>
                  Choisissez l'arrière-plan de la page publique du jeu
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Background Type Selector */}
                <div className="space-y-4">
                  <Label>Type d'arrière-plan</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setAppearance(prev => ({ ...prev, background_type: "color" }))}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        appearance.background_type === "color" 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 mx-auto mb-2" />
                      <p className="text-sm font-medium">Couleur</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAppearance(prev => ({ ...prev, background_type: "image" }))}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        appearance.background_type === "image" 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mx-auto mb-2">
                        <Image className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium">Image</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAppearance(prev => ({ ...prev, background_type: "video" }))}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        appearance.background_type === "video" 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center mx-auto mb-2">
                        <Video className="w-5 h-5 text-red-500" />
                      </div>
                      <p className="text-sm font-medium">Vidéo YouTube</p>
                    </button>
                  </div>
                </div>

                {/* Color Picker */}
                {appearance.background_type === "color" && (
                  <div className="space-y-3">
                    <Label>Couleur de fond</Label>
                    <div className="flex items-center gap-4">
                      <input
                        type="color"
                        value={appearance.background_color}
                        onChange={(e) => setAppearance(prev => ({ ...prev, background_color: e.target.value }))}
                        className="w-16 h-16 rounded-xl cursor-pointer border-2 border-border"
                      />
                      <Input
                        value={appearance.background_color}
                        onChange={(e) => setAppearance(prev => ({ ...prev, background_color: e.target.value }))}
                        placeholder="#1E293B"
                        className="w-32"
                      />
                      <div className="flex gap-2">
                        {["#1E293B", "#0F172A", "#18181B", "#1C1917", "#0C4A6E"].map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setAppearance(prev => ({ ...prev, background_color: color }))}
                            className={`w-8 h-8 rounded-lg border-2 transition-all ${
                              appearance.background_color === color ? "border-primary scale-110" : "border-transparent"
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Image Upload */}
                {appearance.background_type === "image" && (
                  <div className="space-y-3">
                    <Label>Image de fond</Label>
                    <div className="flex items-start gap-4">
                      {appearance.background_image ? (
                        <div className="relative">
                          <img 
                            src={getImageUrl(appearance.background_image)} 
                            alt="Background" 
                            className="w-48 h-32 rounded-xl object-cover border border-border"
                          />
                          <button
                            type="button"
                            onClick={() => setAppearance(prev => ({ ...prev, background_image: "" }))}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="w-48 h-32 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                          {uploadingBg ? (
                            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-6 h-6 text-muted-foreground mb-2" />
                              <span className="text-sm text-muted-foreground">Télécharger</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleBgUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                      <div className="text-sm text-muted-foreground">
                        <p>Format recommandé : JPG, PNG</p>
                        <p>Résolution : 1920x1080 px minimum</p>
                        <p>Taille max : 5 Mo</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* YouTube URL */}
                {appearance.background_type === "video" && (
                  <div className="space-y-3">
                    <Label>URL de la vidéo YouTube</Label>
                    <Input
                      value={appearance.background_video_url}
                      onChange={(e) => setAppearance(prev => ({ ...prev, background_video_url: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                    {extractYoutubeId(appearance.background_video_url) && (
                      <div className="mt-3">
                        <p className="text-sm text-muted-foreground mb-2">Aperçu :</p>
                        <div className="relative w-64 h-36 rounded-xl overflow-hidden">
                          <img 
                            src={`https://img.youtube.com/vi/${extractYoutubeId(appearance.background_video_url)}/maxresdefault.jpg`}
                            alt="YouTube thumbnail"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
                              <Play className="w-6 h-6 text-white ml-1" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground">
                      La vidéo sera lue en boucle sans son en arrière-plan
                    </p>
                  </div>
                )}

                {/* Scratch Card Customization - Only for scratch games */}
                {game?.game_type === "scratch" && (
                  <div className="space-y-6 pt-6 border-t">
                    <div>
                      <h4 className="font-medium text-foreground mb-1 flex items-center gap-2">
                        <Ticket className="w-4 h-4" />
                        Personnalisation de la carte à gratter
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Personnalisez l'apparence de la carte que les joueurs grattent
                      </p>
                    </div>

                    {/* Primary Color */}
                    <div className="space-y-3">
                      <Label>Couleur principale</Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="color"
                          value={appearance.primary_color}
                          onChange={(e) => setAppearance(prev => ({ ...prev, primary_color: e.target.value }))}
                          className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                        />
                        <Input
                          value={appearance.primary_color}
                          onChange={(e) => setAppearance(prev => ({ ...prev, primary_color: e.target.value }))}
                          placeholder="#F59E0B"
                          className="w-32"
                        />
                        <div className="flex gap-2">
                          {["#F59E0B", "#3B82F6", "#10B981", "#8B5CF6", "#EC4899", "#EF4444"].map(color => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => setAppearance(prev => ({ ...prev, primary_color: color }))}
                              className={`w-8 h-8 rounded-lg border-2 transition-all ${
                                appearance.primary_color === color ? "border-primary scale-110" : "border-transparent"
                              }`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Scratch Card Logo */}
                    <div className="space-y-3">
                      <Label>Logo sur la carte (affiché sur la zone à gratter)</Label>
                      <div className="flex items-start gap-4">
                        {appearance.scratch_card_logo ? (
                          <div className="relative">
                            <img 
                              src={getImageUrl(appearance.scratch_card_logo)} 
                              alt="Logo carte" 
                              className="w-32 h-20 rounded-xl object-contain border border-border bg-muted p-2"
                            />
                            <button
                              type="button"
                              onClick={() => setAppearance(prev => ({ ...prev, scratch_card_logo: "" }))}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <label className="w-32 h-20 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                            {uploadingScratchLogo ? (
                              <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                            ) : (
                              <>
                                <Upload className="w-5 h-5 text-muted-foreground mb-1" />
                                <span className="text-xs text-muted-foreground">Logo</span>
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleScratchLogoUpload}
                              className="hidden"
                            />
                          </label>
                        )}
                        <div className="text-sm text-muted-foreground">
                          <p>Votre logo sera affiché sur la carte</p>
                          <p>Taille recommandée : 200x100 px</p>
                          <p>Taille max : 2 Mo</p>
                        </div>
                      </div>
                    </div>

                    {/* Scratch Card Background Image */}
                    <div className="space-y-3">
                      <Label>Image de fond de la carte (optionnel)</Label>
                      <div className="flex items-start gap-4">
                        {appearance.scratch_card_image ? (
                          <div className="relative">
                            <img 
                              src={getImageUrl(appearance.scratch_card_image)} 
                              alt="Fond carte" 
                              className="w-48 h-32 rounded-xl object-cover border border-border"
                            />
                            <button
                              type="button"
                              onClick={() => setAppearance(prev => ({ ...prev, scratch_card_image: "" }))}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <label className="w-48 h-32 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                            {uploadingScratchImage ? (
                              <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
                            ) : (
                              <>
                                <Image className="w-6 h-6 text-muted-foreground mb-2" />
                                <span className="text-sm text-muted-foreground">Image de fond</span>
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleScratchImageUpload}
                              className="hidden"
                            />
                          </label>
                        )}
                        <div className="text-sm text-muted-foreground">
                          <p>Remplace le dégradé de couleur</p>
                          <p>Résolution : 576x384 px recommandée</p>
                          <p>Taille max : 5 Mo</p>
                        </div>
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="p-4 bg-muted/50 rounded-xl">
                      <p className="text-sm font-medium mb-3">Aperçu de la carte</p>
                      <div 
                        className="w-72 h-48 mx-auto rounded-2xl flex flex-col items-center justify-center relative overflow-hidden"
                        style={{
                          background: appearance.scratch_card_image 
                            ? `url(${getImageUrl(appearance.scratch_card_image)})` 
                            : `linear-gradient(135deg, ${appearance.primary_color} 0%, #6366F1 100%)`,
                          backgroundSize: "cover",
                          backgroundPosition: "center"
                        }}
                      >
                        {appearance.scratch_card_image && <div className="absolute inset-0 bg-black/30" />}
                        <div className="relative z-10 text-center">
                          {appearance.scratch_card_logo && (
                            <img 
                              src={getImageUrl(appearance.scratch_card_logo)} 
                              alt="" 
                              className="h-12 mx-auto mb-3 object-contain"
                            />
                          )}
                          <p className="text-white font-bold text-xl drop-shadow-lg">GRATTEZ ICI</p>
                          <p className="text-white/70 text-sm mt-1">pour découvrir votre gain</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Result Screen Customization - Only for scratch games */}
                {game?.game_type === "scratch" && (
                  <div className="space-y-6">
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        Personnalisation des écrans de résultat
                      </h3>
                      <p className="text-sm text-muted-foreground mb-6">
                        Personnalisez l'arrière-plan et les messages affichés après le grattage
                      </p>
                    </div>

                    {/* Lose Screen Customization */}
                    <div className="space-y-4 p-4 bg-red-50 dark:bg-red-950/20 rounded-xl border border-red-200 dark:border-red-800">
                      <h4 className="font-medium text-red-700 dark:text-red-400 flex items-center gap-2">
                        <X className="w-4 h-4" />
                        Écran "Perdu" (par défaut)
                      </h4>
                      
                      {/* Lose Background Image */}
                      <div className="space-y-2">
                        <Label className="text-sm">Image d'arrière-plan (défaite)</Label>
                        <div className="flex items-start gap-4">
                          {appearance.lose_image ? (
                            <div className="relative">
                              <img 
                                src={getImageUrl(appearance.lose_image)} 
                                alt="Fond défaite" 
                                className="w-32 h-20 rounded-lg object-cover border border-border"
                              />
                              <button
                                type="button"
                                onClick={() => setAppearance(prev => ({ ...prev, lose_image: "" }))}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <label className="w-32 h-20 rounded-lg border-2 border-dashed border-red-300 dark:border-red-700 flex flex-col items-center justify-center cursor-pointer hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-950/50 transition-all">
                              <Image className="w-5 h-5 text-red-400 mb-1" />
                              <span className="text-xs text-red-400">Uploader</span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const formData = new FormData();
                                  formData.append("file", file);
                                  try {
                                    const res = await api.post("/upload", formData, {
                                      headers: { "Content-Type": "multipart/form-data" }
                                    });
                                    setAppearance(prev => ({ ...prev, lose_image: res.data.url }));
                                    toast.success("Image uploadée");
                                  } catch (error) {
                                    toast.error("Erreur lors de l'upload");
                                  }
                                }}
                                className="hidden"
                              />
                            </label>
                          )}
                          <div className="flex-1">
                            <Label className="text-sm text-muted-foreground">Ou URL de vidéo YouTube</Label>
                            <Input
                              type="url"
                              placeholder="https://youtube.com/watch?v=..."
                              value={appearance.lose_video_url || ""}
                              onChange={(e) => setAppearance(prev => ({ ...prev, lose_video_url: e.target.value }))}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Lose Messages */}
                      <div className="space-y-2">
                        <Label className="text-sm">Messages de défaite (un par ligne)</Label>
                        <textarea
                          rows={3}
                          placeholder="Pas de chance !&#10;Dommage.. c'était pas loin !&#10;Retentez votre chance !"
                          value={appearance.lose_messages?.join("\n") || ""}
                          onChange={(e) => setAppearance(prev => ({ 
                            ...prev, 
                            lose_messages: e.target.value.split("\n").filter(m => m.trim()) 
                          }))}
                          className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm resize-none"
                        />
                        <p className="text-xs text-muted-foreground">
                          Un message sera choisi aléatoirement pour chaque défaite
                        </p>
                      </div>
                    </div>

                    {/* Win Screen Customization Notice */}
                    <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-xl border border-green-200 dark:border-green-800">
                      <h4 className="font-medium text-green-700 dark:text-green-400 flex items-center gap-2">
                        <Gift className="w-4 h-4" />
                        Écrans "Gagné" (par lot)
                      </h4>
                      <p className="text-sm text-muted-foreground mt-2">
                        Pour personnaliser l'arrière-plan affiché lors du gain d'un lot spécifique, 
                        modifiez le lot dans l'onglet "Lots" et ajoutez une image/vidéo de résultat.
                      </p>
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={handleSaveAppearance}
                    disabled={savingAppearance}
                    className="rounded-full"
                  >
                    {savingAppearance ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : null}
                    Enregistrer l'apparence
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chances Tab - Only for scratch games */}
          {game?.game_type === "scratch" && (
            <TabsContent value="chances">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Configuration */}
                <Card className="bg-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <CreditCard className="w-5 h-5" />
                      Configuration de l'achat de chances
                    </CardTitle>
                    <CardDescription>
                      Définissez le prix et les limites d'achat de chances par les joueurs
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Enable purchase */}
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                      <div>
                        <Label className="text-base font-medium">Autoriser l'achat de chances</Label>
                        <p className="text-sm text-muted-foreground">
                          Les joueurs peuvent acheter des chances en ligne (comme FDJ)
                        </p>
                      </div>
                      <Switch
                        checked={chanceConfig.allow_purchase}
                        onCheckedChange={(checked) => setChanceConfig(prev => ({ ...prev, allow_purchase: checked }))}
                      />
                    </div>

                    {chanceConfig.allow_purchase && (
                      <>
                        {/* Price per chance */}
                        <div className="space-y-3">
                          <Label className="flex items-center gap-2">
                            <Euro className="w-4 h-4" />
                            Prix par chance (€)
                          </Label>
                          <Input
                            type="number"
                            min="0.5"
                            max="100"
                            step="0.5"
                            value={chanceConfig.price_per_chance}
                            onChange={(e) => setChanceConfig(prev => ({ 
                              ...prev, 
                              price_per_chance: parseFloat(e.target.value) || 2.0 
                            }))}
                            className="w-32"
                          />
                          <p className="text-sm text-muted-foreground">
                            Les joueurs paieront ce montant pour chaque grattage
                          </p>
                        </div>

                        {/* Min/Max purchase */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Achat minimum</Label>
                            <Input
                              type="number"
                              min="1"
                              max="10"
                              value={chanceConfig.min_purchase}
                              onChange={(e) => setChanceConfig(prev => ({ 
                                ...prev, 
                                min_purchase: parseInt(e.target.value) || 1 
                              }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Achat maximum</Label>
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={chanceConfig.max_purchase}
                              onChange={(e) => setChanceConfig(prev => ({ 
                                ...prev, 
                                max_purchase: parseInt(e.target.value) || 10 
                              }))}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Save button */}
                    <Button
                      onClick={handleSaveChanceConfig}
                      disabled={savingChanceConfig}
                      className="w-full rounded-full"
                    >
                      {savingChanceConfig && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Enregistrer la configuration
                    </Button>
                  </CardContent>
                </Card>

                {/* Manual attribution */}
                <Card className="bg-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <Gift className="w-5 h-5" />
                      Attribution manuelle de chances
                    </CardTitle>
                    <CardDescription>
                      Offrez des chances à vos clients (ex: 1 chance pour 10€ d'achat)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Téléphone du participant</Label>
                      <Input
                        placeholder="06 12 34 56 78"
                        value={newChance.phone}
                        onChange={(e) => setNewChance(prev => ({ ...prev, phone: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre de chances</Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={newChance.amount}
                          onChange={(e) => setNewChance(prev => ({ ...prev, amount: parseInt(e.target.value) || 1 }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Raison</Label>
                        <Input
                          placeholder="Ex: Achat de 50€"
                          value={newChance.reason}
                          onChange={(e) => setNewChance(prev => ({ ...prev, reason: e.target.value }))}
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleAddChances}
                      disabled={addingChances}
                      className="w-full rounded-full"
                    >
                      {addingChances && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      <Plus className="w-4 h-4 mr-2" />
                      Attribuer les chances
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Participants with wallets */}
              <Card className="bg-card mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Users className="w-5 h-5" />
                    Participants avec des chances
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {participantWallets.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      Aucun participant avec des chances pour l'instant
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 font-medium">Téléphone</th>
                            <th className="text-left py-3 px-4 font-medium">Nom</th>
                            <th className="text-center py-3 px-4 font-medium">Solde</th>
                            <th className="text-center py-3 px-4 font-medium">Total gagné</th>
                            <th className="text-center py-3 px-4 font-medium">Total utilisé</th>
                          </tr>
                        </thead>
                        <tbody>
                          {participantWallets.map((wallet, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="py-3 px-4 font-mono text-sm">{wallet.phone}</td>
                              <td className="py-3 px-4">{wallet.name || "-"}</td>
                              <td className="py-3 px-4 text-center">
                                <Badge variant="outline" className="bg-primary/10">
                                  {wallet.balance} chance{wallet.balance > 1 ? "s" : ""}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-center text-green-600">{wallet.total_earned}</td>
                              <td className="py-3 px-4 text-center text-slate-500">{wallet.total_spent}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <Card className="bg-card">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-foreground">{stats?.total_plays || 0}</p>
                  <p className="text-sm text-muted-foreground">Participations</p>
                </CardContent>
              </Card>
              <Card className="bg-card">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-green-600">{stats?.total_wins || 0}</p>
                  <p className="text-sm text-muted-foreground">Gains</p>
                </CardContent>
              </Card>
              <Card className="bg-card">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-primary">{stats?.win_rate || 0}%</p>
                  <p className="text-sm text-muted-foreground">Taux de gain</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-card">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit' }}>Dernières participations</CardTitle>
              </CardHeader>
              <CardContent>
                {!stats?.recent_plays?.length ? (
                  <p className="text-center text-muted-foreground py-8">
                    Aucune participation pour le moment
                  </p>
                ) : (
                  <div className="space-y-2">
                    {stats.recent_plays.map((play) => (
                      <div 
                        key={play.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          play.won ? 'bg-green-50' : 'bg-muted/30'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-foreground">
                            {play.name || play.phone || play.email || "Anonyme"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(play.timestamp).toLocaleString('fr-FR')}
                          </p>
                        </div>
                        {play.won ? (
                          <Badge className="bg-green-100 text-green-700">
                            {play.prize_name}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Perdu</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
